import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Edit2, Trash2, Folder, FolderOpen, Save, X } from 'lucide-react';

const ICON_OPTIONS = [
  { value: 'folder', label: 'フォルダー', icon: Folder },
  { value: 'folder-open', label: '開いたフォルダー', icon: FolderOpen },
];

const COLOR_OPTIONS = [
  { value: '#6366f1', label: 'インディゴ' },
  { value: '#3b82f6', label: 'ブルー' },
  { value: '#10b981', label: 'グリーン' },
  { value: '#f59e0b', label: 'イエロー' },
  { value: '#ef4444', label: 'レッド' },
  { value: '#8b5cf6', label: 'パープル' },
  { value: '#ec4899', label: 'ピンク' },
  { value: '#64748b', label: 'グレー' },
];

const CategoryManager = ({ onCategoryChange, selectedCategoryId }) => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    color: '#6366f1',
    icon: 'folder'
  });
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('template_categories')
        .select('*')
        .eq('user_id', user.id)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchCategories();
    }
  }, [user, fetchCategories]);

  const createCategory = async () => {
    if (!newCategory.name.trim()) return;

    try {
      const { data, error } = await supabase
        .from('template_categories')
        .insert([{
          user_id: user.id,
          name: newCategory.name.trim(),
          color: newCategory.color,
          icon: newCategory.icon,
          display_order: categories.length
        }])
        .select()
        .single();

      if (error) throw error;

      setCategories([...categories, data]);
      setNewCategory({ name: '', color: '#6366f1', icon: 'folder' });
      setShowNewCategoryForm(false);
    } catch (error) {
      console.error('Error creating category:', error);
      alert('カテゴリーの作成に失敗しました。');
    }
  };

  const updateCategory = async (category) => {
    try {
      const { error } = await supabase
        .from('template_categories')
        .update({
          name: category.name,
          color: category.color,
          icon: category.icon
        })
        .eq('id', category.id);

      if (error) throw error;

      setCategories(categories.map(cat => 
        cat.id === category.id ? category : cat
      ));
      setEditingCategory(null);
    } catch (error) {
      console.error('Error updating category:', error);
      alert('カテゴリーの更新に失敗しました。');
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('このカテゴリーを削除してもよろしいですか？\n関連付けられたテンプレートのカテゴリーは解除されます。')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('template_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      setCategories(categories.filter(cat => cat.id !== categoryId));
      if (selectedCategoryId === categoryId) {
        onCategoryChange(null);
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('カテゴリーの削除に失敗しました。');
    }
  };

  const getIconComponent = (iconName) => {
    const iconOption = ICON_OPTIONS.find(opt => opt.value === iconName);
    return iconOption ? iconOption.icon : Folder;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-2">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">カテゴリー管理</h3>
        <button
          onClick={() => setShowNewCategoryForm(true)}
          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="h-4 w-4 mr-1" />
          新規作成
        </button>
      </div>

      {showNewCategoryForm && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 mb-2">新しいカテゴリー</h4>
          <div className="space-y-3">
            <input
              type="text"
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              placeholder="カテゴリー名"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
            <div className="flex gap-2">
              <select
                value={newCategory.color}
                onChange={(e) => setNewCategory({ ...newCategory, color: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                {COLOR_OPTIONS.map(color => (
                  <option key={color.value} value={color.value}>
                    {color.label}
                  </option>
                ))}
              </select>
              <div
                className="w-10 h-10 rounded-md border border-gray-300"
                style={{ backgroundColor: newCategory.color }}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={createCategory}
                className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Save className="h-4 w-4 mr-1" />
                保存
              </button>
              <button
                onClick={() => {
                  setShowNewCategoryForm(false);
                  setNewCategory({ name: '', color: '#6366f1', icon: 'folder' });
                }}
                className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <X className="h-4 w-4 mr-1" />
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <button
          onClick={() => onCategoryChange(null)}
          className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
            selectedCategoryId === null
              ? 'bg-indigo-50 text-indigo-700 font-medium'
              : 'hover:bg-gray-50 text-gray-700'
          }`}
        >
          すべてのテンプレート
        </button>

        {categories.map((category) => {
          const IconComponent = getIconComponent(category.icon);
          
          if (editingCategory?.id === category.id) {
            return (
              <div key={category.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="space-y-3">
                  <input
                    type="text"
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <div className="flex gap-2">
                    <select
                      value={editingCategory.color}
                      onChange={(e) => setEditingCategory({ ...editingCategory, color: e.target.value })}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {COLOR_OPTIONS.map(color => (
                        <option key={color.value} value={color.value}>
                          {color.label}
                        </option>
                      ))}
                    </select>
                    <div
                      className="w-10 h-10 rounded-md border border-gray-300"
                      style={{ backgroundColor: editingCategory.color }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateCategory(editingCategory)}
                      className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      保存
                    </button>
                    <button
                      onClick={() => setEditingCategory(null)}
                      className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <X className="h-4 w-4 mr-1" />
                      キャンセル
                    </button>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={category.id}
              className={`group flex items-center justify-between px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                selectedCategoryId === category.id
                  ? 'bg-indigo-50'
                  : 'hover:bg-gray-50'
              }`}
              onClick={() => onCategoryChange(category.id)}
            >
              <div className="flex items-center space-x-2">
                <IconComponent
                  className="h-5 w-5"
                  style={{ color: category.color }}
                />
                <span className={`font-medium ${
                  selectedCategoryId === category.id ? 'text-indigo-700' : 'text-gray-700'
                }`}>
                  {category.name}
                </span>
              </div>
              <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingCategory(category);
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <Edit2 className="h-4 w-4 text-gray-600" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteCategory(category.id);
                  }}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CategoryManager;